import React from "react";

const S = {
  wrapper:      { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Simple Linear Regression: scatter, line, residuals
// ─────────────────────────────────────────────────────────────────
function DiagramSimpleLinearRegression() {
  const W = 900, H = 340;

  // Fixed data points: (x, y) pairs with underlying truth y = 1 + 1.5x + ε
  const pts = [
    {x:0.5,y:1.8},{x:1.0,y:3.2},{x:1.5,y:2.9},{x:2.0,y:4.1},{x:2.5,y:4.8},
    {x:3.0,y:5.2},{x:3.5,y:6.8},{x:4.0,y:6.5},{x:4.5,y:7.9},{x:5.0,y:8.4},
    {x:1.2,y:2.4},{x:2.8,y:5.5},{x:3.8,y:7.2},{x:0.8,y:2.1},{x:4.2,y:7.4},
  ];

  // OLS estimates: β̂₁ = Σ(xᵢ-x̄)(yᵢ-ȳ)/Σ(xᵢ-x̄)², β̂₀ = ȳ - β̂₁·x̄
  const n = pts.length;
  const xbar = pts.reduce((s,p)=>s+p.x,0)/n;
  const ybar = pts.reduce((s,p)=>s+p.y,0)/n;
  const Sxy = pts.reduce((s,p)=>s+(p.x-xbar)*(p.y-ybar),0);
  const Sxx = pts.reduce((s,p)=>s+(p.x-xbar)**2,0);
  const b1 = Sxy/Sxx;               // ≈ 1.47
  const b0 = ybar - b1*xbar;        // ≈ 0.97
  const yhat = x => b0 + b1*x;

  // Plot area  ── left panel 0‥560
  const aX=54, aY=32, aW=480, aH=230;
  const xMin=0, xMax=5.6, yMin=0, yMax=10;
  const toX = v => aX + (v-xMin)/(xMax-xMin)*aW;
  const toY = v => aY + aH - (v-yMin)/(yMax-yMin)*aH;

  // Regression line endpoints
  const lineX1=xMin, lineX2=xMax;

  // Right panel
  const panX=576, panW=W-panX-14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Simple Linear Regression — OLS Fit, Residuals, and Least-Squares Line</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* ── LEFT PANEL ── */}
        <rect x={10} y={8} width={556} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={548} height={22} rx={3} fill="#1F3864"/>
        <text x={292} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          ŷ = {b0.toFixed(2)} + {b1.toFixed(2)}x  — OLS minimises Σ(yᵢ − ŷᵢ)²
        </text>

        {/* Grid */}
        {[2,4,6,8,10].map(v=>(
          <g key={v}>
            <line x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#eee" strokeWidth="0.8"/>
            <text x={aX-6} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#aaa">{v}</text>
          </g>
        ))}
        {[1,2,3,4,5].map(v=>(
          <g key={v}>
            <line x1={toX(v)} y1={aY} x2={toX(v)} y2={aY+aH} stroke="#eee" strokeWidth="0.8"/>
            <text x={toX(v)} y={aY+aH+14} textAnchor="middle" fontSize="9" fill="#aaa">{v}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#888" strokeWidth="1.5"/>
        <line x1={aX} y1={aY}    x2={aX}    y2={aY+aH} stroke="#888" strokeWidth="1.5"/>
        <text x={aX+aW/2} y={aY+aH+28} textAnchor="middle" fontSize="11" fill="#555">x (predictor)</text>
        <text x={aX-36} y={aY+aH/2} textAnchor="middle" fontSize="11" fill="#555"
          transform={`rotate(-90,${aX-36},${aY+aH/2})`}>y (response)</text>

        {/* Residual lines (draw before points so points sit on top) */}
        {pts.map((p,i)=>{
          const fitted = yhat(p.x);
          const isAbove = p.y >= fitted;
          return (
            <line key={i} x1={toX(p.x)} y1={toY(p.y)} x2={toX(p.x)} y2={toY(fitted)}
              stroke={isAbove?'#27ae60':'#c0392b'} strokeWidth="1.4" strokeDasharray="3,2" opacity="0.7"/>
          );
        })}

        {/* Regression line */}
        <line x1={toX(lineX1)} y1={toY(yhat(lineX1))}
              x2={toX(lineX2)} y2={toY(yhat(lineX2))}
              stroke="#2E75B6" strokeWidth="2.8"/>

        {/* Data points */}
        {pts.map((p,i)=>(
          <circle key={i} cx={toX(p.x)} cy={toY(p.y)} r={5}
            fill="#1F3864" stroke="#fff" strokeWidth="1.2" opacity="0.85"/>
        ))}

        {/* ȳ horizontal line */}
        <line x1={aX} y1={toY(ybar)} x2={aX+aW} y2={toY(ybar)}
          stroke="#e07b00" strokeWidth="1.2" strokeDasharray="6,3"/>
        <text x={aX+aW-4} y={toY(ybar)-5} textAnchor="end" fontSize="9" fill="#e07b00" fontWeight="700">ȳ={ybar.toFixed(2)}</text>

        {/* Slope annotation arrow */}
        <line x1={toX(4)} y1={toY(yhat(4))} x2={toX(5)} y2={toY(yhat(4))} stroke="#2E75B6" strokeWidth="1.2"/>
        <line x1={toX(5)} y1={toY(yhat(4))} x2={toX(5)} y2={toY(yhat(5))} stroke="#2E75B6" strokeWidth="1.2"/>
        <text x={toX(4.5)} y={toY(yhat(4))+13} textAnchor="middle" fontSize="9" fill="#2E75B6">Δx=1</text>
        <text x={toX(5)+18} y={toY(yhat(4.5))} fontSize="9" fill="#2E75B6">β̂₁={b1.toFixed(2)}</text>

        {/* Legend */}
        <circle cx={aX+8} cy={aY+aH+38} r={5} fill="#1F3864"/>
        <text x={aX+18} y={aY+aH+42} fontSize="9.5" fill="#444">data point</text>
        <line x1={aX+80} y1={aY+aH+38} x2={aX+96} y2={aY+aH+38} stroke="#2E75B6" strokeWidth="2.5"/>
        <text x={aX+100} y={aY+aH+42} fontSize="9.5" fill="#444">OLS line</text>
        <line x1={aX+168} y1={aY+aH+38} x2={aX+184} y2={aY+aH+38} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="3,2"/>
        <text x={aX+188} y={aY+aH+42} fontSize="9.5" fill="#444">residual (+)</text>
        <line x1={aX+268} y1={aY+aH+38} x2={aX+284} y2={aY+aH+38} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="3,2"/>
        <text x={aX+288} y={aY+aH+42} fontSize="9.5" fill="#444">residual (−)</text>

        {/* ── RIGHT PANEL ── */}
        <rect x={panX} y={8} width={panW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={panX+panW/2} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">OLS Formulas</text>

        {[
          { y:34,  bg:'#EBF3FB', bc:'#2E75B6', title:'Model',
            lines:['yᵢ = β₀ + β₁xᵢ + εᵢ','εᵢ ~ N(0,σ²) i.i.d.','β₀: intercept','β₁: slope'] },
          { y:130, bg:'#f0fff4', bc:'#27ae60', title:'OLS Estimates',
            lines:['β̂₁ = Sxy / Sxx','β̂₀ = ȳ − β̂₁x̄','Sxy=Σ(xᵢ−x̄)(yᵢ−ȳ)','Sxx=Σ(xᵢ−x̄)²'] },
          { y:228, bg:'#fff7ee', bc:'#e07b00', title:'Residuals',
            lines:['êᵢ = yᵢ − ŷᵢ','Σêᵢ = 0  always','Σxᵢêᵢ = 0  always','MSE = SSE/(n−2)'] },
        ].map(({y,bg,bc,title,lines})=>(
          <g key={y}>
            <rect x={panX+8} y={y} width={panW-16} height={90} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={panX+18} y={y+16} fontSize="12" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={panX+18} y={y+32+j*16} fontSize="10.5"
                fill={j===0?'#1F3864':'#555'}
                fontFamily={j<=1?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j<=1?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        OLS (Ordinary Least Squares) finds β̂₀ and β̂₁ that minimise the sum of squared vertical residuals Σêᵢ². Green dashed lines are positive residuals (point above line); red dashed lines are negative. The orange horizontal line is ȳ. By construction, the OLS residuals always sum to zero and are uncorrelated with x.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Partitioning Variance: SST = SSR + SSE, and R²
// ─────────────────────────────────────────────────────────────────
function DiagramRSquared() {
  const W = 900, H = 330;

  // Reuse same data
  const pts = [
    {x:0.5,y:1.8},{x:1.0,y:3.2},{x:1.5,y:2.9},{x:2.0,y:4.1},{x:2.5,y:4.8},
    {x:3.0,y:5.2},{x:3.5,y:6.8},{x:4.0,y:6.5},{x:4.5,y:7.9},{x:5.0,y:8.4},
    {x:1.2,y:2.4},{x:2.8,y:5.5},{x:3.8,y:7.2},{x:0.8,y:2.1},{x:4.2,y:7.4},
  ];
  const n=pts.length;
  const xbar=pts.reduce((s,p)=>s+p.x,0)/n;
  const ybar=pts.reduce((s,p)=>s+p.y,0)/n;
  const Sxy=pts.reduce((s,p)=>s+(p.x-xbar)*(p.y-ybar),0);
  const Sxx=pts.reduce((s,p)=>s+(p.x-xbar)**2,0);
  const b1=Sxy/Sxx, b0=ybar-b1*xbar;
  const yhat=x=>b0+b1*x;
  const SST=pts.reduce((s,p)=>s+(p.y-ybar)**2,0);
  const SSE=pts.reduce((s,p)=>s+(p.y-yhat(p.x))**2,0);
  const SSR=SST-SSE;
  const R2=SSR/SST;

  // Left: decomposition diagram using one highlighted point
  // Pick the point near x=3.0 (index 5) for annotation
  const hiPt = pts[5]; // x=3.0, y=5.2
  const hiHat = yhat(hiPt.x);

  const aX=54, aY=32, aW=350, aH=230;
  const xMin=0, xMax=5.6, yMin=0, yMax=10;
  const toX = v => aX+(v-xMin)/(xMax-xMin)*aW;
  const toY = v => aY+aH-(v-yMin)/(yMax-yMin)*aH;

  // Right panel: bar chart of SST/SSR/SSE + R² gauge
  const bPanX=450, bPanW=W-bPanX-14, bPanH=H-20;
  const barVals=[
    {label:'SST',val:SST,color:'#1F3864'},
    {label:'SSR',val:SSR,color:'#27ae60'},
    {label:'SSE',val:SSE,color:'#c0392b'},
  ];
  const maxBar=SST;
  const barAreaX=bPanX+60, barAreaY=48, barAreaW=bPanW-80, barAreaH=130;
  const barW=barAreaW/4;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Variance Decomposition — SST = SSR + SSE and the Coefficient of Determination R²</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* LEFT PANEL */}
        <rect x={10} y={8} width={430} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={422} height={22} rx={3} fill="#1F3864"/>
        <text x={224} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Decomposing one observation's deviation from ȳ
        </text>

        {/* Grid */}
        {[2,4,6,8].map(v=>(
          <g key={v}>
            <line x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)} stroke="#f0f0f0" strokeWidth="0.8"/>
            <text x={aX-5} y={toY(v)+4} textAnchor="end" fontSize="9" fill="#bbb">{v}</text>
          </g>
        ))}
        <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#aaa" strokeWidth="1.5"/>
        <line x1={aX} y1={aY}    x2={aX}    y2={aY+aH} stroke="#aaa" strokeWidth="1.5"/>

        {/* All points (light) */}
        {pts.map((p,i)=>(
          <circle key={i} cx={toX(p.x)} cy={toY(p.y)} r={4}
            fill="#1F3864" stroke="#fff" strokeWidth="1" opacity="0.35"/>
        ))}

        {/* ȳ line */}
        <line x1={aX} y1={toY(ybar)} x2={aX+aW} y2={toY(ybar)}
          stroke="#e07b00" strokeWidth="1.5" strokeDasharray="5,3"/>
        <text x={aX+aW-2} y={toY(ybar)-5} textAnchor="end" fontSize="9" fill="#e07b00" fontWeight="700">ȳ</text>

        {/* Regression line */}
        <line x1={toX(0)} y1={toY(yhat(0))} x2={toX(5.5)} y2={toY(yhat(5.5))}
          stroke="#2E75B6" strokeWidth="2.2"/>

        {/* Highlighted point */}
        <circle cx={toX(hiPt.x)} cy={toY(hiPt.y)} r={7}
          fill="#1F3864" stroke="#e07b00" strokeWidth="2.2"/>

        {/* SST bracket: yᵢ to ȳ */}
        {(() => {
          const x0=toX(hiPt.x)-22;
          const y1=toY(hiPt.y), y2=toY(ybar);
          return (
            <g>
              <line x1={x0+4} y1={y1} x2={x0+4} y2={y2} stroke="#1F3864" strokeWidth="2"/>
              <line x1={x0} y1={y1} x2={x0+8} y2={y1} stroke="#1F3864" strokeWidth="1.5"/>
              <line x1={x0} y1={y2} x2={x0+8} y2={y2} stroke="#1F3864" strokeWidth="1.5"/>
              <text x={x0-4} y={(y1+y2)/2+4} textAnchor="end" fontSize="10" fill="#1F3864" fontWeight="700">SST</text>
              <text x={x0-4} y={(y1+y2)/2+16} textAnchor="end" fontSize="9" fill="#555">(yᵢ−ȳ)</text>
            </g>
          );
        })()}

        {/* SSR bracket: ŷᵢ to ȳ */}
        {(() => {
          const x0=toX(hiPt.x)+18;
          const y1=toY(hiHat), y2=toY(ybar);
          return (
            <g>
              <line x1={x0+4} y1={y1} x2={x0+4} y2={y2} stroke="#27ae60" strokeWidth="2"/>
              <line x1={x0} y1={y1} x2={x0+8} y2={y1} stroke="#27ae60" strokeWidth="1.5"/>
              <line x1={x0} y1={y2} x2={x0+8} y2={y2} stroke="#27ae60" strokeWidth="1.5"/>
              <text x={x0+16} y={(y1+y2)/2+4} fontSize="10" fill="#27ae60" fontWeight="700">SSR</text>
              <text x={x0+16} y={(y1+y2)/2+16} fontSize="9" fill="#555">(ŷᵢ−ȳ)</text>
            </g>
          );
        })()}

        {/* SSE bracket: yᵢ to ŷᵢ */}
        {(() => {
          const x0=toX(hiPt.x)+50;
          const y1=toY(hiPt.y), y2=toY(hiHat);
          return (
            <g>
              <line x1={x0+4} y1={y1} x2={x0+4} y2={y2} stroke="#c0392b" strokeWidth="2"/>
              <line x1={x0} y1={y1} x2={x0+8} y2={y1} stroke="#c0392b" strokeWidth="1.5"/>
              <line x1={x0} y1={y2} x2={x0+8} y2={y2} stroke="#c0392b" strokeWidth="1.5"/>
              <text x={x0+16} y={(y1+y2)/2+4} fontSize="10" fill="#c0392b" fontWeight="700">SSE</text>
              <text x={x0+16} y={(y1+y2)/2+16} fontSize="9" fill="#555">(yᵢ−ŷᵢ)</text>
            </g>
          );
        })()}

        {/* Axis labels */}
        <text x={aX+aW/2} y={aY+aH+26} textAnchor="middle" fontSize="10" fill="#555">x</text>
        <text x={aX-36} y={aY+aH/2} textAnchor="middle" fontSize="10" fill="#555"
          transform={`rotate(-90,${aX-36},${aY+aH/2})`}>y</text>

        {/* Equation strip */}
        <rect x={14} y={H-38} width={422} height={26} rx={3} fill="#EBF3FB" stroke="#2E75B6"/>
        <text x={224} y={H-22} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">
          SST = SSR + SSE   (yᵢ−ȳ) = (ŷᵢ−ȳ) + (yᵢ−ŷᵢ)
        </text>

        {/* RIGHT PANEL */}
        <rect x={bPanX} y={8} width={bPanW} height={bPanH} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={bPanX+4} y={12} width={bPanW-8} height={22} rx={3} fill="#1F3864"/>
        <text x={bPanX+bPanW/2} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Sum of Squares &amp; R² = {R2.toFixed(3)}
        </text>

        {/* Bar chart */}
        {barVals.map(({label,val,color},i)=>{
          const bx=barAreaX+i*(barW+14);
          const bh=(val/maxBar)*barAreaH;
          const by=barAreaY+barAreaH-bh;
          return (
            <g key={label}>
              <rect x={bx} y={by} width={barW} height={bh} rx={3} fill={color} opacity="0.85"/>
              <text x={bx+barW/2} y={by-5} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">
                {val.toFixed(1)}
              </text>
              <text x={bx+barW/2} y={barAreaY+barAreaH+14} textAnchor="middle" fontSize="11" fill={color} fontWeight="700">
                {label}
              </text>
            </g>
          );
        })}
        <line x1={barAreaX-4} y1={barAreaY+barAreaH} x2={barAreaX+3*(barW+14)} y2={barAreaY+barAreaH}
          stroke="#aaa" strokeWidth="1.5"/>

        {/* R² gauge */}
        {(() => {
          const gx=bPanX+20, gy=210, gw=bPanW-40, gh=32;
          const fillW=R2*gw;
          return (
            <g>
              <text x={bPanX+bPanW/2} y={gy-6} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">
                R² = SSR/SST = {R2.toFixed(3)}
              </text>
              <rect x={gx} y={gy} width={gw} height={gh} rx={4} fill="#f0f0f0" stroke="#ccd9ea"/>
              <rect x={gx} y={gy} width={fillW} height={gh} rx={4} fill="#27ae60" opacity="0.8"/>
              <text x={gx+fillW/2} y={gy+gh/2+5} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">
                {(R2*100).toFixed(1)}% explained
              </text>
              <text x={gx+fillW+(gw-fillW)/2} y={gy+gh/2+5} textAnchor="middle" fontSize="10" fill="#888">
                {((1-R2)*100).toFixed(1)}% residual
              </text>
            </g>
          );
        })()}

        {/* Formulas */}
        {[
          'R² = SSR/SST = 1 − SSE/SST',
          'R² ∈ [0,1];  R²=1 → perfect fit',
          'Adjusted R² = 1−(SSE/(n−p−1))/(SST/(n−1))',
        ].map((t,i)=>(
          <text key={i} x={bPanX+bPanW/2} y={262+i*18} textAnchor="middle"
            fontSize="10" fill={i===0?'#27ae60':'#555'}
            fontFamily="'Courier New',monospace" fontWeight={i===0?'700':'400'}>{t}</text>
        ))}
      </svg>
      <div style={S.dCaption}>
        Every observation's deviation from ȳ (SST) splits into the part explained by the regression line (SSR, green) and the unexplained residual (SSE, red). R² = SSR/SST measures the fraction of total variance captured by the model. Here R² = {R2.toFixed(3)}, meaning {(R2*100).toFixed(1)}% of the variation in y is explained by x.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Residual Diagnostics: four residual plots
// ─────────────────────────────────────────────────────────────────
function DiagramResidualDiagnostics() {
  const W = 900, H = 340;

  // Four mini-panels across the width
  const panW = (W-28)/4, panH = H-20;
  const aH=170, aY_offset=44;

  // Shared helper
  const mkAxes = (px, aX_local, aW_local) => {
    const aY=aY_offset;
    return { aX:px+aX_local, aY, aW:aW_local, aH };
  };

  // Panel configs
  const panels = [
    {
      title:'Good: Random scatter', color:'#27ae60', fill:'#f0fff4',
      subtitle:'No pattern → assumptions met',
      // Residuals: random around 0
      pts:[
        {x:1,r:0.4},{x:1.5,r:-0.6},{x:2,r:0.2},{x:2.5,r:0.8},{x:3,r:-0.3},
        {x:3.5,r:0.5},{x:4,r:-0.7},{x:4.5,r:0.1},{x:5,r:-0.4},{x:5.5,r:0.6},
        {x:6,r:0.3},{x:6.5,r:-0.5},{x:7,r:0.7},{x:7.5,r:-0.2},{x:8,r:0.4},
      ],
    },
    {
      title:'Bad: Heteroscedasticity', color:'#e07b00', fill:'#fff7ee',
      subtitle:'Variance increases with ŷ',
      pts:[
        {x:1,r:0.05},{x:1.5,r:-0.1},{x:2,r:0.18},{x:2.5,r:-0.22},{x:3,r:0.35},
        {x:3.5,r:-0.4},{x:4,r:0.55},{x:4.5,r:-0.6},{x:5,r:0.8},{x:5.5,r:-0.85},
        {x:6,r:1.0},{x:6.5,r:-1.1},{x:7,r:1.3},{x:7.5,r:-0.9},{x:8,r:1.5},
      ],
    },
    {
      title:'Bad: Non-linearity', color:'#c0392b', fill:'#fff0f0',
      subtitle:'Curved pattern → missing x²',
      pts:[
        {x:1,r:-1.0},{x:1.5,r:-0.7},{x:2,r:-0.5},{x:2.5,r:-0.2},{x:3,r:0.2},
        {x:3.5,r:0.5},{x:4,r:0.8},{x:4.5,r:0.7},{x:5,r:0.5},{x:5.5,r:0.2},
        {x:6,r:-0.2},{x:6.5,r:-0.5},{x:7,r:-0.8},{x:7.5,r:-1.0},{x:8,r:-1.3},
      ],
    },
    {
      title:'Bad: Autocorrelation', color:'#9b59b6', fill:'#f5d0ff',
      subtitle:'Runs pattern → time series data',
      pts:[
        {x:1,r:0.2},{x:1.5,r:0.5},{x:2,r:0.8},{x:2.5,r:1.0},{x:3,r:0.9},
        {x:3.5,r:0.6},{x:4,r:0.3},{x:4.5,r:-0.1},{x:5,r:-0.4},{x:5.5,r:-0.8},
        {x:6,r:-1.0},{x:6.5,r:-0.9},{x:7,r:-0.6},{x:7.5,r:-0.3},{x:8,r:0.1},
      ],
    },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Residual Diagnostic Plots — Identifying Violations of OLS Assumptions</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {panels.map(({title,color,fill,subtitle,pts},pi)=>{
          const px=10+pi*(panW+2.7);
          const aX=px+22, aY=aY_offset, aW=panW-30, aH2=aH;
          const xMin=0.5, xMax=8.5;
          const rMin=-1.6, rMax=1.6;
          const toX = v => aX+(v-xMin)/(xMax-xMin)*aW;
          const toR = r => aY+aH2/2 - (r/(rMax-rMin/2))*aH2*0.7;

          return (
            <g key={pi}>
              <rect x={px} y={8} width={panW} height={panH} rx={5} fill={fill} stroke={color} strokeWidth="1.8"/>
              <rect x={px+4} y={12} width={panW-8} height={28} rx={3} fill={color}/>
              <text x={px+panW/2} y={25} textAnchor="middle" fontSize="10.5" fill="#fff" fontWeight="700">{title}</text>
              <text x={px+panW/2} y={36} textAnchor="middle" fontSize="9" fill="#ffffffcc">{subtitle}</text>

              {/* Plot area border */}
              <rect x={aX} y={aY} width={aW} height={aH2} rx={3} fill="#fff" stroke="#ddd"/>

              {/* Zero line */}
              <line x1={aX} y1={aY+aH2/2} x2={aX+aW} y2={aY+aH2/2}
                stroke={color} strokeWidth="1.2" strokeDasharray="4,3" opacity="0.6"/>

              {/* Upper/lower dashed bounds */}
              {[-1,1].map(s=>(
                <line key={s} x1={aX} y1={toR(s*1.2)} x2={aX+aW} y2={toR(s*1.2)}
                  stroke="#ddd" strokeWidth="0.8" strokeDasharray="3,3"/>
              ))}

              {/* Points */}
              {pts.map(({x,r},i)=>(
                <circle key={i} cx={toX(x)} cy={toR(r)} r={3.5}
                  fill={color} stroke="#fff" strokeWidth="0.8" opacity="0.85"/>
              ))}

              {/* Axis labels */}
              <text x={aX+aW/2} y={aY+aH2+14} textAnchor="middle" fontSize="9" fill="#666">Fitted ŷ</text>
              <text x={aX-14} y={aY+aH2/2+4} textAnchor="middle" fontSize="9" fill="#666"
                transform={`rotate(-90,${aX-14},${aY+aH2/2+4})`}>Residual ê</text>
              <text x={aX+aW/2} y={aY+aH2/2+4} textAnchor="middle" fontSize="8.5" fill={color} opacity="0.5">0</text>

              {/* Bottom annotation */}
              <rect x={px+6} y={aY+aH2+22} width={panW-12} height={panH-aY-aH2-30} rx={3}
                fill="#fff" stroke={color} strokeWidth="0.8"/>
              {pi===0 && (
                <>
                  <text x={px+panW/2} y={aY+aH2+36} textAnchor="middle" fontSize="9" fill={color} fontWeight="700">✓ Homoscedastic</text>
                  <text x={px+panW/2} y={aY+aH2+50} textAnchor="middle" fontSize="8.5" fill="#555">✓ Linear relationship</text>
                  <text x={px+panW/2} y={aY+aH2+64} textAnchor="middle" fontSize="8.5" fill="#555">✓ Independence</text>
                </>
              )}
              {pi===1 && (
                <>
                  <text x={px+panW/2} y={aY+aH2+36} textAnchor="middle" fontSize="9" fill={color} fontWeight="700">✗ Var(εᵢ) not constant</text>
                  <text x={px+panW/2} y={aY+aH2+50} textAnchor="middle" fontSize="8.5" fill="#555">Fix: WLS or log(y)</text>
                  <text x={px+panW/2} y={aY+aH2+64} textAnchor="middle" fontSize="8.5" fill="#555">SE estimates invalid</text>
                </>
              )}
              {pi===2 && (
                <>
                  <text x={px+panW/2} y={aY+aH2+36} textAnchor="middle" fontSize="9" fill={color} fontWeight="700">✗ E[ε|x] ≠ 0</text>
                  <text x={px+panW/2} y={aY+aH2+50} textAnchor="middle" fontSize="8.5" fill="#555">Fix: add x², interaction</text>
                  <text x={px+panW/2} y={aY+aH2+64} textAnchor="middle" fontSize="8.5" fill="#555">Biased β estimates</text>
                </>
              )}
              {pi===3 && (
                <>
                  <text x={px+panW/2} y={aY+aH2+36} textAnchor="middle" fontSize="9" fill={color} fontWeight="700">✗ Cov(εᵢ,εⱼ) ≠ 0</text>
                  <text x={px+panW/2} y={aY+aH2+50} textAnchor="middle" fontSize="8.5" fill="#555">Fix: GLS, lag terms</text>
                  <text x={px+panW/2} y={aY+aH2+64} textAnchor="middle" fontSize="8.5" fill="#555">Durbin–Watson test</text>
                </>
              )}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        The residual vs. fitted plot is the most important regression diagnostic. Random scatter around zero (panel 1) confirms all key OLS assumptions. A fan shape (panel 2) signals heteroscedasticity — unequal error variance. A curved pattern (panel 3) means the linear form is misspecified. A wave pattern (panel 4) suggests autocorrelated errors, common in time series. Each violation invalidates different aspects of OLS inference.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Multiple Regression: geometry and coefficient interpretation
// ─────────────────────────────────────────────────────────────────
function DiagramMultipleRegression() {
  const W = 900, H = 340;

  // Left: coefficient interpretation graphic (visualise partial slopes)
  // Show two predictors x1, x2 with different standardised coefficients
  const coefs = [
    { name:'Intercept β₀', val: 3.50, se:0.42, t:8.33, p:'<.001', color:'#1F3864' },
    { name:'x₁ (hours study)', val: 1.20, se:0.18, t:6.67, p:'<.001', color:'#27ae60' },
    { name:'x₂ (prior score)',  val: 0.55, se:0.10, t:5.50, p:'<.001', color:'#2E75B6' },
    { name:'x₃ (group: B)',     val:-0.30, se:0.22, t:-1.36,p:' .175', color:'#e07b00' },
    { name:'x₄ (study×prior)', val: 0.08, se:0.04, t:2.00, p:' .047', color:'#9b59b6' },
  ];

  // Left panel: coefficient plot (forest plot style)
  const lpW=490, lpH=H-20;
  const rowH=44;
  const rowY = (i) => 46 + i*rowH;
  const dotX=lpW-130, ciW=80;
  const valMin=-0.6, valMax=1.5;
  const toValX = v => 90 + (v-valMin)/(valMax-valMin)*(dotX-100);
  const zeroX = toValX(0);

  // Right panel: model equation + R² + F-test info
  const rpX=506, rpW=W-rpX-14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Multiple Linear Regression — Coefficient Plot, Significance, and Model Summary</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* LEFT PANEL */}
        <rect x={10} y={8} width={lpW} height={lpH} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={lpW-8} height={22} rx={3} fill="#1F3864"/>
        <text x={lpW/2+10} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          Coefficient Plot — ŷ = β₀ + β₁x₁ + β₂x₂ + β₃x₃ + β₄(x₁×x₂)
        </text>

        {/* Column headers */}
        <text x={14} y={42} fontSize="10" fill="#888" fontWeight="700">Predictor</text>
        <text x={zeroX} y={42} textAnchor="middle" fontSize="10" fill="#888" fontWeight="700">Estimate ± 95% CI</text>
        <text x={dotX+30} y={42} textAnchor="middle" fontSize="10" fill="#888" fontWeight="700">t</text>
        <text x={dotX+75} y={42} textAnchor="middle" fontSize="10" fill="#888" fontWeight="700">p</text>

        {/* Zero reference line */}
        <line x1={zeroX} y1={38} x2={zeroX} y2={46+5*rowH-10} stroke="#ddd" strokeWidth="1.2" strokeDasharray="4,3"/>
        <text x={zeroX} y={46+5*rowH+2} textAnchor="middle" fontSize="8.5" fill="#aaa">0</text>

        {/* Grid */}
        {[-0.5,0,0.5,1.0,1.5].map(v=>(
          <g key={v}>
            <line x1={toValX(v)} y1={38} x2={toValX(v)} y2={46+5*rowH-10}
              stroke={v===0?'#ccc':'#f0f0f0'} strokeWidth={v===0?1:0.7}/>
            <text x={toValX(v)} y={46+5*rowH+14} textAnchor="middle" fontSize="8" fill="#ccc">{v}</text>
          </g>
        ))}

        {coefs.map(({name,val,se,t,p,color},i)=>{
          const cy=rowY(i)+rowH/2;
          const cx=toValX(val);
          // 95% CI: ±1.96*se (approximate)
          const lo=toValX(val-1.96*se), hi=toValX(val+1.96*se);
          const sig=parseFloat(p)<0.05;
          return (
            <g key={i}>
              {/* Row bg */}
              <rect x={14} y={rowY(i)+2} width={lpW-18} height={rowH-4} rx={3}
                fill={i%2===0?'#fafcff':'#fff'} opacity="0.6"/>
              {/* Name */}
              <text x={18} y={cy+4} fontSize="10" fill={color} fontWeight="700">{name}</text>
              {/* CI line */}
              <line x1={lo} y1={cy} x2={hi} y2={cy} stroke={color} strokeWidth="2" opacity="0.7"/>
              <line x1={lo} y1={cy-5} x2={lo} y2={cy+5} stroke={color} strokeWidth="1.5"/>
              <line x1={hi} y1={cy-5} x2={hi} y2={cy+5} stroke={color} strokeWidth="1.5"/>
              {/* Dot */}
              <circle cx={cx} cy={cy} r={sig?6:5}
                fill={sig?color:'#fff'} stroke={color} strokeWidth="2"/>
              {/* t and p */}
              <text x={dotX+30} y={cy+4} textAnchor="middle" fontSize="10"
                fill={color} fontFamily="'Courier New',monospace" fontWeight="700">{t.toFixed(2)}</text>
              <text x={dotX+75} y={cy+4} textAnchor="middle" fontSize="10"
                fill={sig?color:'#aaa'} fontFamily="'Courier New',monospace"
                fontWeight={sig?'700':'400'}>{p}</text>
              {sig && <text x={dotX+95} y={cy+4} fontSize="10" fill={color}>*</text>}
            </g>
          );
        })}

        {/* Legend */}
        <circle cx={14+lpW/2-40} cy={H-28} r={6} fill="#2E75B6"/>
        <text x={14+lpW/2-30} y={H-24} fontSize="9" fill="#555">p&lt;0.05 (filled)</text>
        <circle cx={14+lpW/2+40} cy={H-28} r={5} fill="#fff" stroke="#888" strokeWidth="1.5"/>
        <text x={14+lpW/2+50} y={H-24} fontSize="9" fill="#555">p≥0.05 (open)</text>

        {/* RIGHT PANEL */}
        <rect x={rpX} y={8} width={rpW} height={lpH} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={rpX+rpW/2} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Model Summary
        </text>

        {[
          { y:34,  bg:'#EBF3FB', bc:'#2E75B6', title:'Model Fit',
            lines:['R² = 0.847','Adj. R² = 0.831','RMSE = 0.63','n = 80'] },
          { y:134, bg:'#f0fff4', bc:'#27ae60', title:'F-test (overall)',
            lines:['H₀: all βⱼ = 0','F = 52.4, df=4,75','p < 0.001','Model is significant'] },
          { y:234, bg:'#fff7ee', bc:'#e07b00', title:'Partial effects',
            lines:['β̂ⱼ: effect of xⱼ','holding all other','predictors fixed','("ceteris paribus")'] },
        ].map(({y,bg,bc,title,lines})=>(
          <g key={y}>
            <rect x={rpX+8} y={y} width={rpW-16} height={92} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={rpX+18} y={y+16} fontSize="12" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={rpX+18} y={y+32+j*17} fontSize="10.5"
                fill={j<2?'#1F3864':'#555'}
                fontFamily={j<2?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j<2?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        A coefficient plot (forest plot) displays each estimate with its 95% confidence interval. Filled circles indicate significance at α=0.05; open circles do not. In multiple regression, each β̂ⱼ measures the partial effect of xⱼ — the change in ŷ per unit increase in xⱼ holding all other predictors constant. The F-test assesses whether the model as a whole explains significant variance, while individual t-tests assess each predictor.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Bias-Variance Trade-off: underfitting vs overfitting
// ─────────────────────────────────────────────────────────────────
function DiagramBiasVarianceTradeoff() {
  const W = 900, H = 330;

  // True function: y = sin(x) + 0.15x, x in [0, 2π]
  const trueFn = x => Math.sin(x) + 0.15*x;
  const noisePts = [
    {x:0.3,y:0.40},{x:0.7,y:0.78},{x:1.1,y:0.95},{x:1.5,y:1.10},{x:1.9,y:0.95},
    {x:2.3,y:0.74},{x:2.7,y:0.60},{x:3.1,y:0.32},{x:3.5,y:0.55},{x:3.9,y:0.70},
    {x:4.3,y:0.45},{x:4.7,y:0.18},{x:5.1,y:-0.20},{x:5.5,y:0.10},{x:5.9,y:0.58},
  ];

  // Three model fits displayed in panels
  const panW = (W-28)/3, panH = H-20;
  const aH=190, aY_off=46;
  const xMn=0, xMx=6.3, yMn=-1.2, yMx=1.8;

  const toX=(px,v)=>px+26+(v-xMn)/(xMx-xMn)*(panW-46);
  const toY=(v)=>aY_off+aH-(v-yMn)/(yMx-yMn)*aH;

  // Simple linear fit (underfitting)
  const linFit = x => 0.25 + 0.06*x;  // approximate poor linear fit

  // Good polynomial fit approximation (smooth sine-like)
  const goodFit = x => trueFn(x);

  // Overfit: passes through all points (wiggly -- approximate)
  const overFit = x => {
    // interpolate wildly between points using a simple hack
    const base = trueFn(x);
    const noise = 0.45*Math.sin(8*x+0.3)*Math.exp(-0.3*Math.abs(x-3));
    return base + noise;
  };

  const models = [
    { title:'Underfitting (degree 1)', color:'#c0392b', fill:'#fff0f0',
      label:'High Bias, Low Variance', fit:linFit,
      desc:['Too simple — misses','the true pattern.','Bias² dominates MSE.','Increase model complexity.'] },
    { title:'Good fit (degree ~5)', color:'#27ae60', fill:'#f0fff4',
      label:'Low Bias, Low Variance', fit:goodFit,
      desc:['Captures the pattern','without chasing noise.','Bias²+Var balanced.','Best generalisation.'] },
    { title:'Overfitting (degree 14)', color:'#9b59b6', fill:'#f5d0ff',
      label:'Low Bias, High Variance', fit:overFit,
      desc:['Memorises training','data including noise.','Variance dominates.','Poor on new data.'] },
  ];

  // Curve points for true function
  const truePts=Array.from({length:80},(_,i)=>{ const x=xMn+(xMx-xMn)*i/79; return {x,y:trueFn(x)}; });

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Bias–Variance Trade-off — Underfitting, Good Fit, and Overfitting</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {models.map(({title,color,fill,label,fit,desc},pi)=>{
          const px=10+pi*(panW+4);
          const fitPts=Array.from({length:80},(_,i)=>{ const x=xMn+(xMx-xMn)*i/79; return {x,y:fit(x)}; });
          const fitPoly=fitPts.map(({x,y})=>`${toX(px,x).toFixed(1)},${toY(y).toFixed(1)}`).join(' ');
          const truePoly=truePts.map(({x,y})=>`${toX(px,x).toFixed(1)},${toY(y).toFixed(1)}`).join(' ');

          return (
            <g key={pi}>
              <rect x={px} y={8} width={panW} height={panH} rx={5} fill={fill} stroke={color} strokeWidth="1.8"/>
              <rect x={px+4} y={12} width={panW-8} height={30} rx={3} fill={color}/>
              <text x={px+panW/2} y={26} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">{title}</text>
              <text x={px+panW/2} y={38} textAnchor="middle" fontSize="9.5" fill="#ffffffcc">{label}</text>

              {/* Plot area */}
              <rect x={px+24} y={aY_off} width={panW-46} height={aH} rx={3} fill="#fff" stroke="#ddd"/>

              {/* Zero line */}
              <line x1={toX(px,xMn)} y1={toY(0)} x2={toX(px,xMx)} y2={toY(0)}
                stroke="#eee" strokeWidth="0.8"/>

              {/* True function (grey) */}
              <polyline points={truePoly} fill="none" stroke="#bbb" strokeWidth="1.5" strokeDasharray="5,3"/>

              {/* Model fit */}
              <polyline points={fitPoly} fill="none" stroke={color} strokeWidth="2.5"/>

              {/* Data points */}
              {noisePts.map(({x,y},i)=>(
                <circle key={i} cx={toX(px,x)} cy={toY(y)} r={3.5}
                  fill="#1F3864" stroke="#fff" strokeWidth="0.8" opacity="0.75"/>
              ))}

              {/* x/y labels */}
              <text x={toX(px,xMx/2)} y={aY_off+aH+14} textAnchor="middle" fontSize="9" fill="#888">x</text>
              <text x={toX(px,xMn)-12} y={aY_off+aH/2+4} textAnchor="middle" fontSize="9" fill="#888"
                transform={`rotate(-90,${toX(px,xMn)-12},${aY_off+aH/2+4})`}>y</text>

              {/* Description */}
              <rect x={px+6} y={aY_off+aH+22} width={panW-12} height={panH-aY_off-aH-30} rx={3}
                fill="#fff" stroke={color} strokeWidth="0.8"/>
              {desc.map((ln,j)=>(
                <text key={j} x={px+panW/2} y={aY_off+aH+36+j*17}
                  textAnchor="middle" fontSize="9.5" fill={j===0?color:'#555'}
                  fontWeight={j===0?'700':'400'}>{ln}</text>
              ))}
            </g>
          );
        })}

        {/* Bottom legend */}
        <line x1={10} y1={H-8} x2={34} y2={H-8} stroke="#bbb" strokeWidth="1.5" strokeDasharray="5,3"/>
        <text x={38} y={H-4} fontSize="9" fill="#888">True function</text>
        <circle cx={150} cy={H-8} r={3.5} fill="#1F3864"/>
        <text x={158} y={H-4} fontSize="9" fill="#888">Training data</text>
      </svg>
      <div style={S.dCaption}>
        The grey dashed curve is the unknown true function. Blue/green/purple is the fitted model. An underfit model (left) has high bias — it misses the true shape regardless of the sample. An overfit model (right) has high variance — it chases the noise, fitting training data perfectly but generalising poorly. Regularisation (ridge, lasso) and cross-validation help find the middle ground.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Logistic Regression: sigmoid, decision boundary, odds
// ─────────────────────────────────────────────────────────────────
function DiagramLogisticRegression() {
  const W = 900, H = 330;

  // Sigmoid function
  const sigmoid = z => 1/(1+Math.exp(-z));

  // Logistic model: P(y=1|x) = σ(−3 + 1.2x)
  const b0=-3, b1=1.2;
  const prob = x => sigmoid(b0+b1*x);
  const boundary = -b0/b1; // x where p=0.5 ≈ 2.5

  // Fake binary data points
  const pts0=[{x:0.3},{x:0.8},{x:1.1},{x:1.4},{x:1.7},{x:0.6},{x:1.9},{x:0.4}]; // y=0
  const pts1=[{x:2.8},{x:3.2},{x:3.6},{x:4.0},{x:4.4},{x:3.0},{x:4.8},{x:3.8}]; // y=1
  const ptsMix=[{x:2.1,y:1},{x:2.4,y:0},{x:2.6,y:1},{x:2.9,y:0}]; // near boundary

  const aX=54, aY=32, aW=380, aH=210;
  const xMn=0, xMx=5.5;
  const toX = v => aX+(v-xMn)/(xMx-xMn)*aW;
  const toP = p => aY+aH-p*aH;

  const sigmoidPts=Array.from({length:100},(_,i)=>{
    const x=xMn+(xMx-xMn)*i/99;
    return `${toX(x).toFixed(1)},${toP(prob(x)).toFixed(1)}`;
  }).join(' ');

  // Right panel: odds and log-odds
  const rpX=470, rpW=W-rpX-14;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Logistic Regression — Sigmoid Curve, Decision Boundary, and Log-Odds</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>

        {/* LEFT PANEL */}
        <rect x={10} y={8} width={450} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <rect x={14} y={12} width={442} height={22} rx={3} fill="#9b59b6"/>
        <text x={234} y={27} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">
          P(Y=1|x) = σ(β₀ + β₁x) = σ(−3 + 1.2x)
        </text>

        {/* Grid */}
        {[0,0.25,0.5,0.75,1.0].map(p=>(
          <g key={p}>
            <line x1={aX} y1={toP(p)} x2={aX+aW} y2={toP(p)} stroke="#eee" strokeWidth="0.8"/>
            <text x={aX-5} y={toP(p)+4} textAnchor="end" fontSize="9" fill="#aaa">{p.toFixed(2)}</text>
          </g>
        ))}
        <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#aaa" strokeWidth="1.5"/>
        <line x1={aX} y1={aY}    x2={aX}    y2={aY+aH} stroke="#aaa" strokeWidth="1.5"/>

        {/* Shaded regions */}
        <rect x={aX} y={aY} width={toX(boundary)-aX} height={aH}
          fill="#c0392b" opacity="0.05"/>
        <rect x={toX(boundary)} y={aY} width={aX+aW-toX(boundary)} height={aH}
          fill="#27ae60" opacity="0.05"/>

        {/* Sigmoid curve */}
        <polyline points={sigmoidPts} fill="none" stroke="#9b59b6" strokeWidth="2.8"/>

        {/* Decision boundary */}
        <line x1={toX(boundary)} y1={aY} x2={toX(boundary)} y2={aY+aH}
          stroke="#e07b00" strokeWidth="2" strokeDasharray="6,3"/>
        <text x={toX(boundary)+4} y={aY+16} fontSize="9.5" fill="#e07b00" fontWeight="700">
          x={boundary.toFixed(1)}
        </text>
        <text x={toX(boundary)+4} y={aY+30} fontSize="9" fill="#e07b00">p=0.5</text>

        {/* p=0.5 line */}
        <line x1={aX} y1={toP(0.5)} x2={aX+aW} y2={toP(0.5)}
          stroke="#e07b00" strokeWidth="1" strokeDasharray="4,3" opacity="0.6"/>

        {/* Data points y=0 */}
        {pts0.map(({x},i)=>(
          <rect key={i} x={toX(x)-5} y={toP(0)-5} width={10} height={10}
            fill="#c0392b" stroke="#fff" strokeWidth="1" opacity="0.8" rx={2}/>
        ))}
        {/* Data points y=1 */}
        {pts1.map(({x},i)=>(
          <circle key={i} cx={toX(x)} cy={toP(1)+8} r={5}
            fill="#27ae60" stroke="#fff" strokeWidth="1" opacity="0.8"/>
        ))}
        {/* Mixed points */}
        {ptsMix.map(({x,y},i)=>(
          y===1
            ? <circle key={i} cx={toX(x)} cy={toP(1)+8} r={5} fill="#27ae60" stroke="#fff" strokeWidth="1" opacity="0.65"/>
            : <rect key={i} x={toX(x)-5} y={toP(0)-5} width={10} height={10} fill="#c0392b" stroke="#fff" strokeWidth="1" opacity="0.65" rx={2}/>
        ))}

        {/* Class labels */}
        <text x={toX(boundary)-10} y={aY+aH-10} textAnchor="end" fontSize="10" fill="#c0392b" fontWeight="700">
          Predict 0
        </text>
        <text x={toX(boundary)+10} y={aY+aH-10} fontSize="10" fill="#27ae60" fontWeight="700">
          Predict 1
        </text>

        {/* Legend */}
        <rect x={aX+4} y={aY+aH+18} width={10} height={10} fill="#c0392b" rx={2}/>
        <text x={aX+18} y={aY+aH+27} fontSize="9.5" fill="#444">y=0 class</text>
        <circle cx={aX+100} cy={aY+aH+23} r={5} fill="#27ae60"/>
        <text x={aX+110} y={aY+aH+27} fontSize="9.5" fill="#444">y=1 class</text>
        <text x={aX+aW/2} y={aY+aH+42} textAnchor="middle" fontSize="10" fill="#555">x (predictor)</text>

        {/* RIGHT PANEL */}
        <rect x={rpX} y={8} width={rpW} height={H-16} rx={5} fill="#fff" stroke="#ccd9ea"/>
        <text x={rpX+rpW/2} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Logit &amp; Odds
        </text>

        {[
          { y:32,  bg:'#f5d0ff', bc:'#9b59b6', title:'Logit link',
            lines:['logit(p) = log(p/1-p)','         = β₀ + β₁x','Linear in log-odds','not in probability'] },
          { y:132, bg:'#EBF3FB', bc:'#2E75B6', title:'Odds ratio',
            lines:['OR = exp(β₁)','One unit ↑ in x','multiplies odds by','exp(β̂₁) = e^1.2≈3.32'] },
          { y:232, bg:'#f0fff4', bc:'#27ae60', title:'Estimation',
            lines:['Maximum likelihood','No closed-form soln','Iterative: IRLS or','gradient descent'] },
        ].map(({y,bg,bc,title,lines})=>(
          <g key={y}>
            <rect x={rpX+8} y={y} width={rpW-16} height={92} rx={4} fill={bg} stroke={bc} strokeWidth="1.3"/>
            <text x={rpX+18} y={y+16} fontSize="12" fill={bc} fontWeight="700">{title}</text>
            {lines.map((ln,j)=>(
              <text key={j} x={rpX+18} y={y+32+j*17} fontSize="10.5"
                fill={j<2?'#1F3864':'#555'}
                fontFamily={j<=1?"'Courier New',monospace":"'Georgia',serif"}
                fontWeight={j<=1?'700':'400'}>{ln}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>
        Logistic regression models the log-odds of a binary outcome as a linear function of predictors. The sigmoid (logistic) function maps any real number to a probability in (0,1). The decision boundary x={boundary.toFixed(1)} is where P(Y=1|x)=0.5. Each coefficient β̂ is interpreted as a change in log-odds; exponentiating gives the odds ratio, which is easier to interpret substantively.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 7 — Regularisation: Ridge, Lasso, and coefficient paths
// ─────────────────────────────────────────────────────────────────
function DiagramRegularisation() {
  const W = 900, H = 340;

  // Coefficient shrinkage paths for Ridge and Lasso (schematic, 4 predictors)
  const lambdas = Array.from({length:40},(_,i)=>i*0.25);
  // OLS values (at λ=0)
  const olsCoefs = [1.8, -1.2, 0.6, -0.3];
  const coefColors = ['#2E75B6','#c0392b','#27ae60','#e07b00'];
  const coefNames = ['β₁','β₂','β₃','β₄'];

  // Ridge: βⱼ(λ) = βⱼ_ols / (1+λ)  (schematic)
  const ridgePath = (j) => lambdas.map(l=>({
    l, v: olsCoefs[j]/(1+l*0.6)
  }));
  // Lasso: soft-thresholding, reaches 0 at different λ
  const thresholds = [3.5, 2.4, 1.5, 0.8]; // λ at which each goes to 0
  const lassoPath = (j) => lambdas.map(l=>({
    l, v: Math.abs(olsCoefs[j])>0
      ? (l<thresholds[j] ? olsCoefs[j]*(1-l/thresholds[j]) : 0)
      : 0
  }));

  const panW=(W-28)/2;
  const aH=180, aX_off=50, aY_off=46, aW_rel=panW-70;
  const lMax=lambdas[lambdas.length-1];
  const vMin=-1.4, vMax=2.0;

  const mkPath=(px, pathFn, j)=>{
    const pts=pathFn(j).map(({l,v})=>({
      x:px+aX_off+l/lMax*aW_rel,
      y:aY_off+aH-(v-vMin)/(vMax-vMin)*aH
    }));
    return pts.map(p=>`${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Regularisation — Ridge (L2) and Lasso (L1) Coefficient Shrinkage Paths</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',display:'block'}}>
        {[
          {title:'Ridge Regression (L2)', subtitle:'β shrink toward 0, never exactly 0',
           bc:'#2E75B6', fill:'#EBF3FB', pathFn:ridgePath,
           penalty:'λΣβⱼ²',note:'All coefs shrink together'},
          {title:'Lasso Regression (L1)', subtitle:'Sparse — some β hit exactly 0',
           bc:'#c0392b', fill:'#fff0f0', pathFn:lassoPath,
           penalty:'λΣ|βⱼ|',note:'Automatic feature selection'},
        ].map(({title,subtitle,bc,fill,pathFn,penalty,note},pi)=>{
          const px=10+pi*(panW+8);
          const aX=px+aX_off, aY=aY_off, aW=aW_rel;
          const toX=l=>aX+l/lMax*aW;
          const toY=v=>aY+aH-(v-vMin)/(vMax-vMin)*aH;

          return (
            <g key={pi}>
              <rect x={px} y={8} width={panW} height={H-16} rx={5} fill={fill} stroke={bc} strokeWidth="1.8"/>
              <rect x={px+4} y={12} width={panW-8} height={30} rx={3} fill={bc}/>
              <text x={px+panW/2} y={26} textAnchor="middle" fontSize="12" fill="#fff" fontWeight="700">{title}</text>
              <text x={px+panW/2} y={38} textAnchor="middle" fontSize="9.5" fill="#ffffffcc">{subtitle}</text>

              {/* Plot border */}
              <rect x={aX} y={aY} width={aW} height={aH} rx={3} fill="#fff" stroke="#ddd"/>

              {/* Grid + zero line */}
              {[-1,0,1,2].map(v=>(
                <g key={v}>
                  <line x1={aX} y1={toY(v)} x2={aX+aW} y2={toY(v)}
                    stroke={v===0?'#ccc':'#eee'} strokeWidth={v===0?1.2:0.8}/>
                  <text x={aX-4} y={toY(v)+4} textAnchor="end" fontSize="8" fill="#aaa">{v}</text>
                </g>
              ))}

              {/* Lambda axis ticks */}
              {[0,2,4,6,8,10].map(v=>(
                <g key={v}>
                  <line x1={toX(v)} y1={aY+aH} x2={toX(v)} y2={aY+aH+5} stroke="#ccc" strokeWidth="1"/>
                  <text x={toX(v)} y={aY+aH+15} textAnchor="middle" fontSize="8.5" fill="#888">{v}</text>
                </g>
              ))}

              {/* Axes */}
              <line x1={aX} y1={aY+aH} x2={aX+aW} y2={aY+aH} stroke="#aaa" strokeWidth="1.5"/>
              <line x1={aX} y1={aY}    x2={aX}    y2={aY+aH} stroke="#aaa" strokeWidth="1"/>

              {/* Coefficient paths */}
              {olsCoefs.map((_,j)=>(
                <polyline key={j} points={mkPath(px,pathFn,j)}
                  fill="none" stroke={coefColors[j]} strokeWidth="2.2" opacity="0.9"/>
              ))}

              {/* OLS labels at λ=0 */}
              {olsCoefs.map((v,j)=>(
                <text key={j} x={aX-4} y={toY(v)+4} textAnchor="end" fontSize="9"
                  fill={coefColors[j]} fontWeight="700">{coefNames[j]}</text>
              ))}

              {/* Axis labels */}
              <text x={aX+aW/2} y={aY+aH+28} textAnchor="middle" fontSize="10" fill="#555">λ (regularisation strength)</text>
              <text x={aX-34} y={aY+aH/2} textAnchor="middle" fontSize="10" fill="#555"
                transform={`rotate(-90,${aX-34},${aY+aH/2})`}>Coefficient value</text>

              {/* Info strip */}
              <rect x={px+6} y={aY+aH+36} width={panW-12} height={H-aY-aH-48} rx={3}
                fill="#fff" stroke={bc} strokeWidth="0.8"/>
              <text x={px+panW/2} y={aY+aH+52} textAnchor="middle" fontSize="10.5"
                fill={bc} fontFamily="'Courier New',monospace" fontWeight="700">
                Objective: Σ(yᵢ−ŷᵢ)² + {penalty}
              </text>
              <text x={px+panW/2} y={aY+aH+70} textAnchor="middle" fontSize="9.5" fill="#555">
                {note}
              </text>
              {pi===0 && (
                <text x={px+panW/2} y={aY+aH+87} textAnchor="middle" fontSize="9.5" fill="#555">
                  Closed form: β̂ = (XᵀX + λI)⁻¹Xᵀy
                </text>
              )}
              {pi===1 && (
                <text x={px+panW/2} y={aY+aH+87} textAnchor="middle" fontSize="9.5" fill="#555">
                  No closed form — coordinate descent or LARS
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Regularisation adds a penalty on coefficient size to the OLS objective, shrinking estimates toward zero and reducing variance at the cost of some bias. Ridge (L2) shrinks all coefficients smoothly but never exactly to zero. Lasso (L1) produces sparse solutions — some coefficients hit exactly zero, performing automatic feature selection. The optimal λ is chosen by cross-validation.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function RegressionAnalysis() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Regression Analysis</h1>

      <p style={S.p}>
        Regression analysis quantifies the relationship between a response variable y and one or more predictors x₁, …, xₚ. It answers two questions simultaneously: how much does y change when xⱼ changes (by one unit, holding others fixed), and how well does our model predict y from x? Starting with simple linear regression — fitting a straight line by least squares — the framework extends naturally to multiple predictors, binary outcomes, polynomial terms, and regularised models. Regression is the backbone of empirical research across science, economics, and machine learning.
      </p>

      <InfoBox
        title="Regression at a Glance"
        items={[
          "Simple linear regression: y = β₀ + β₁x + ε — one predictor, one line, minimise Σêᵢ² by OLS.",
          "Multiple regression: y = β₀ + β₁x₁ + … + βₚxₚ + ε — each β̂ⱼ is a partial effect, others held fixed.",
          "OLS assumptions (LINE): Linear relationship, Independent errors, Normal errors, Equal variance (homoscedasticity).",
          "R²: fraction of variance in y explained by the model. Adjusted R² penalises adding weak predictors.",
          "Residuals êᵢ = yᵢ − ŷᵢ are the primary diagnostic tool — plot them against ŷ and against each predictor.",
          "Logistic regression: for binary y, model the log-odds as a linear function; use MLE not OLS.",
          "Regularisation (Ridge/Lasso): add a penalty on |β| to control overfitting and handle multicollinearity.",
          "The bias–variance trade-off: complex models have low bias but high variance; simpler models the reverse.",
        ]}
      />

      <h2 style={S.h2}>Simple Linear Regression — The OLS Line</h2>
      <DiagramSimpleLinearRegression />
      <Table2 rows={[
        ["SLR model",           "yᵢ = β₀ + β₁xᵢ + εᵢ, where εᵢ ~ N(0,σ²) i.i.d.\nβ₀: expected y when x=0; β₁: expected change in y per unit x"],
        ["OLS estimators",      "β̂₁ = Sxy/Sxx = Σ(xᵢ−x̄)(yᵢ−ȳ)/Σ(xᵢ−x̄)²\nβ̂₀ = ȳ − β̂₁x̄  — line always passes through (x̄, ȳ)"],
        ["Gauss–Markov theorem","Under LINE assumptions, OLS is BLUE: Best Linear Unbiased Estimator\nNo other linear unbiased estimator has smaller variance"],
        ["Standard errors",     "SE(β̂₁) = √(MSE/Sxx), where MSE = SSE/(n−2)\nSE(β̂₀) = SE(β̂₁) · √(Σxᵢ²/n)"],
        ["t-test for slope",    "H₀: β₁=0, T = β̂₁/SE(β̂₁) ~ t(n−2)\nReject H₀ if |T| > t_{α/2, n−2}; slope CI: β̂₁ ± t*·SE(β̂₁)"],
        ["Prediction interval", "ŷ ± t*·SE_pred, where SE_pred = √(MSE(1+1/n+(x*−x̄)²/Sxx))\nWider than CI for E[y]: accounts for future single-observation noise"],
      ]} />

      <h2 style={S.h2}>Variance Decomposition and R²</h2>
      <DiagramRSquared />
      <Table2 rows={[
        ["SST",                 "Σ(yᵢ−ȳ)² — total variation in y around its mean\ndf = n−1"],
        ["SSR",                 "Σ(ŷᵢ−ȳ)² — variation explained by the regression\ndf = p (number of predictors)"],
        ["SSE",                 "Σ(yᵢ−ŷᵢ)² = Σêᵢ² — unexplained (residual) variation\ndf = n−p−1; MSE = SSE/(n−p−1) estimates σ²"],
        ["R²",                  "R² = SSR/SST = 1−SSE/SST ∈ [0,1]\nFor SLR: R² = r²  (square of Pearson correlation)"],
        ["Adjusted R²",         "R²_adj = 1 − (SSE/(n−p−1))/(SST/(n−1))\nPenalises adding predictors; can decrease if a predictor adds little"],
        ["F-statistic",         "F = (SSR/p) / (SSE/(n−p−1)) = MSR/MSE ~ F(p, n−p−1) under H₀\nTests H₀: all β₁=…=βₚ=0 simultaneously"],
      ]} />

      <h2 style={S.h2}>Residual Diagnostics</h2>
      <DiagramResidualDiagnostics />
      <Table2 rows={[
        ["Residual vs. fitted",  "Plot êᵢ vs ŷᵢ — should show random scatter around 0\nPatterns indicate non-linearity or heteroscedasticity"],
        ["Heteroscedasticity",   "Var(εᵢ) non-constant — fan shape in residual plot\nFix: WLS (weighted least squares), Box-Cox transform, robust SEs"],
        ["Non-linearity",        "Curved pattern in residuals — linear model is misspecified\nFix: add polynomial terms, splines, or transform predictors"],
        ["Normality of errors",  "Check with Q-Q plot: residuals should lie on y=x line\nNeeded for exact t/F distributions; CLT provides robustness for large n"],
        ["Outliers & leverage",  "Studentised residuals > ±3 are outliers; Cook's D > 1 flags influential points\nHigh-leverage: points with unusual x-values pull the line toward them"],
        ["Durbin–Watson test",   "DW ≈ 2: no autocorrelation; DW < 1.5: positive autocorrelation\nFix: include lag variables, use GLS (Generalised Least Squares)"],
      ]} />

      <h2 style={S.h2}>Multiple Linear Regression</h2>
      <DiagramMultipleRegression />
      <Table2 rows={[
        ["Matrix form",         "y = Xβ + ε, where X is n×(p+1) design matrix with 1 in first column\nβ̂ = (XᵀX)⁻¹Xᵀy — requires XᵀX invertible (no perfect multicollinearity)"],
        ["Partial effect",      "β̂ⱼ: change in ŷ per unit increase in xⱼ, holding all other xₖ fixed\nCannot interpret marginal plots without controlling for confounders"],
        ["Multicollinearity",   "Correlated predictors inflate SE(β̂ⱼ) and make individual tests unreliable\nCheck VIF (Variance Inflation Factor): VIF > 10 is problematic"],
        ["Interaction terms",   "Include xⱼ·xₖ when the effect of xⱼ on y depends on the value of xₖ\nCentre predictors first; interpret main effects only at mean of other"],
        ["Dummy variables",     "k-level categorical → include k−1 dummies; omitted level is baseline\nCoefficient = difference in mean y vs. baseline, adjusted for other x"],
        ["Model selection",     "AIC = 2k − 2ℓ; BIC = k·log(n) − 2ℓ — lower is better\nForward/backward stepwise, best subset, LASSO, or cross-validation"],
      ]} />

      <h2 style={S.h2}>Bias–Variance Trade-off</h2>
      <DiagramBiasVarianceTradeoff />
      <Table2 rows={[
        ["Bias–variance decomp.","E[(y−ŷ)²] = Bias(ŷ)² + Var(ŷ) + σ²  (irreducible noise)\nComplexity ↑ → Bias ↓, Var ↑; optimum balances the two"],
        ["Training vs test error","Training error always decreases with complexity\nTest error has a U-shape — use held-out validation set"],
        ["Cross-validation",     "k-fold CV: split data into k folds, train on k−1, evaluate on 1, rotate\nMSEᶜᵥ = (1/k)Σ MSEₖ; use to choose polynomial degree, λ, etc."],
        ["Polynomial regression", "Add xⱼ² or higher powers to capture curvature; still linear in β\nDegree choice: CV or visual inspection of residuals"],
        ["Splines",              "Piecewise polynomial with knots — flexible yet smooth\nNatural splines, B-splines, smoothing splines trade complexity for fit"],
        ["Regularisation choice","Ridge: predictors correlated, want to keep all\nLasso: want sparse model with automatic feature selection"],
      ]} />

      <h2 style={S.h2}>Logistic Regression</h2>
      <DiagramLogisticRegression />
      <Table2 rows={[
        ["Logistic model",      "log(p/(1−p)) = β₀ + β₁x₁ + … + βₚxₚ  (log-odds is linear)\nP(Y=1|x) = 1/(1+e^(−Xβ)) via sigmoid function"],
        ["Odds ratio",          "exp(β̂ⱼ): multiplicative change in odds per unit increase in xⱼ\nOR > 1: xⱼ increases probability; OR < 1: decreases it"],
        ["Estimation",          "MLE via IRLS (Iteratively Reweighted Least Squares) or gradient descent\nNo closed-form solution; likelihood is concave → unique global maximum"],
        ["Deviance",            "−2 log L(fitted model) — analogous to SSE in linear regression\nNull deviance: intercept-only; residual deviance: fitted model"],
        ["McFadden's pseudo-R²","1 − log L(model)/log L(null) ∈ [0,1]\n0.2–0.4 is considered good for logistic models"],
        ["Classification metrics","Accuracy, precision, recall, F1 from confusion matrix\nROC curve / AUC: performance across all decision thresholds"],
      ]} />

      <h2 style={S.h2}>Regularisation: Ridge and Lasso</h2>
      <DiagramRegularisation />
      <Table2 rows={[
        ["Ridge (L2)",          "min Σ(yᵢ−ŷᵢ)² + λΣβⱼ²  — closed form: β̂=(XᵀX+λI)⁻¹Xᵀy\nAll coefs shrink toward 0 but none exactly 0; good with correlated x"],
        ["Lasso (L1)",          "min Σ(yᵢ−ŷᵢ)² + λΣ|βⱼ|  — no closed form; coordinate descent\nSparse solutions: some β̂ⱼ=0 exactly → automatic feature selection"],
        ["Elastic net",         "Combines L1 and L2: λ₁Σ|βⱼ| + λ₂Σβⱼ²\nBenefits of both: sparse + handles correlated groups of predictors"],
        ["Choosing λ",          "Cross-validation: compute CV error over a grid of λ; pick minimum\nOne-standard-error rule: pick largest λ within 1 SE of min CV error"],
        ["Standardisation",     "Regularisation penalises β magnitude — predictors must be standardised\nDivide each xⱼ by its SD; interpret β in SD units"],
        ["Ridge vs OLS",        "Ridge reduces variance more than it increases bias when XᵀX is ill-conditioned\nAlways strictly better than OLS in terms of MSE when λ > 0 (Stein paradox)"],
      ]} />

      <InfoBox
        title="Key Takeaways — Regression Analysis"
        items={[
          "OLS minimises Σêᵢ² and under LINE assumptions produces the Best Linear Unbiased Estimator (Gauss–Markov).",
          "Always plot residuals vs. fitted values — this single plot catches non-linearity, heteroscedasticity, and outliers.",
          "R² measures in-sample fit; use adjusted R² or cross-validation to compare models with different numbers of predictors.",
          "In multiple regression, β̂ⱼ is a partial effect — it controls for all other predictors in the model.",
          "Multicollinearity inflates standard errors; check VIF. Perfect collinearity makes (XᵀX) non-invertible.",
          "Logistic regression is the standard for binary outcomes; coefficients are log-odds, exp(β̂) are odds ratios.",
          "Regularisation (Ridge/Lasso) reduces overfitting by shrinking coefficients; the penalty λ is chosen by cross-validation.",
          "The bias–variance trade-off is fundamental: complexity reduces bias but increases variance; test error has a U-shape.",
        ]}
      />
    </div>
  );
}